import React from 'react'
import { createRoot } from 'react-dom/client'
import { Route, Mountain, Calendar, Tent, Trophy, TrainTrack, Bike } from 'lucide-react'
import { UnifiedRoute } from '../../services/firebase/routes'
import { 
  getTotalDistance,
  getTotalElevationGain,
  calculateUnpavedPercentage,
  getRouteTypeDisplay,
  getRouteStageCount
} from '../../utils/routeDisplay'

interface RouteHoverPanelProps {
  route: UnifiedRoute
  position: { x: number; y: number }
  onClick?: () => void
}

export function RouteHoverPanel({ 
  route, 
  position,
  onClick
}: RouteHoverPanelProps) {

  // Format stats
  const distance = getTotalDistance(route)
  const formattedDistance = Math.ceil(distance).toLocaleString()
  const elevationGain = getTotalElevationGain(route)
  const unpavedPercentage = calculateUnpavedPercentage(route)
  const stagesCount = getRouteStageCount(route)

  // Get route type icon
  const getRouteIcon = () => {
    const routeTypeDisplay = getRouteTypeDisplay(route.routeType)
    switch (routeTypeDisplay) {
      case 'Bikepacking':
        return <Tent size={16} stroke="white" strokeWidth={2.5} />
      case 'Event':
        return <Calendar size={16} stroke="white" strokeWidth={2.5} />
      case 'Permanent':
        return <Trophy size={16} stroke="white" strokeWidth={2.5} />
      case 'Rail Trail':
        return <TrainTrack size={16} stroke="white" strokeWidth={2.5} />
      case 'Bike Path':
        return <Bike size={16} stroke="white" strokeWidth={2.5} />
      default:
        return <Route size={16} stroke="white" strokeWidth={2.5} />
    }
  }

  return (
    <div 
      className="route-cluster-hover-panel"
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: 'translateX(-50%) translateY(-100%) translateY(-10px)',
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.6), rgba(31,41,55,0.7))',
        border: '1px solid rgba(255, 255, 255, 0.06)',
        borderRadius: '12px',
        padding: '8px',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255,255,255,0.05)',
        zIndex: 1000,
        pointerEvents: 'auto',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div 
        className="route-preview-item"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '6px',
          borderRadius: '8px',
          cursor: 'pointer',
          transition: 'all 0.2s',
          background: 'rgba(255, 255, 255, 0.03)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(16, 185, 129, 0.1)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)'
        }}
        onClick={onClick}
      >
        {/* Route Icon */}
        <div style={{
          width: '32px',
          height: '32px',
          background: 'rgba(16, 185, 129, 0.4)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          border: '1px solid rgba(16, 185, 129, 0.6)',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          {getRouteIcon()}
        </div>
        
        {/* Route Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '13px',
            fontWeight: 600,
            color: 'white',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            marginBottom: '4px'
          }}>
            {route.name}
          </div>
          
          {/* Stats Row */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '11px',
            color: '#9ca3af',
            whiteSpace: 'nowrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Route className="w-2.5 h-2.5 text-emerald-400" />
              <span>{formattedDistance}km</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Mountain className="w-2.5 h-2.5 text-emerald-400" />
              <span>{elevationGain.toLocaleString()}m</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <svg className="w-2.5 h-2.5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span>{unpavedPercentage}% unp.</span>
            </div>
            {stagesCount > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <svg className="w-2.5 h-2.5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                <span>{stagesCount} {stagesCount === 1 ? 'stage' : 'stages'}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// Helper function to create and mount the panel
export function createRouteHoverPanel(
  container: HTMLElement,
  route: UnifiedRoute,
  position: { x: number; y: number },
  onPhotoLoad?: (routeId: string) => Promise<string[]>
) {
  const root = createRoot(container)
  
  let onMouseEnter: (() => void) | null = null
  let onMouseLeave: (() => void) | null = null
  let onClickCallback: (() => void) | null = null
  
  const render = () => {
    root.render(
      <div
        onMouseEnter={() => onMouseEnter?.()}
        onMouseLeave={() => onMouseLeave?.()}
      >
        <RouteHoverPanel
          route={route}
          position={position}
          onClick={() => onClickCallback?.()}
        />
      </div>
    )
  }
  
  render()
  
  return {
    updatePosition: (newPosition: { x: number; y: number }) => {
      position = newPosition
      render()
    },
    setOnMouseEnter: (callback: () => void) => {
      onMouseEnter = callback
    },
    setOnMouseLeave: (callback: () => void) => {
      onMouseLeave = callback
    },
    setOnClick: (callback: () => void) => {
      onClickCallback = callback
    },
    destroy: () => {
      root.unmount()
    }
  }
}